import type { MotionRecipe } from '../animation/procedural/recipes';
import { TEMPLATES, type Binding, type MotionPath, type MotionSlot, type MotionStepInput, type TemplateId } from '../animation/procedural/spec';
import type { ConflictPolicy } from '../domain/motionOps';
import type { Scene, SceneObject } from '../domain/schemas';
import { createId } from '../lib/ids';
import { Observable } from '../lib/store';
import type { MotionDraft } from '../prompt/motionCommands';

/**
 * Motion Composer state (Phase 3C). UI state only: it is never saved in the project and never
 * recorded in undo history (previewing and editing parameters change nothing). It is
 * cleared when a project is opened or closed. Applying the plan is one project change.
 */
export interface ComposerState {
  slots: MotionSlot[];
  steps: MotionStepInput[];
  seed: number;
  totalSeconds: number | null;
  binding: Binding;
  policy: ConflictPolicy;
  allowExtend: boolean;
  newAnimationName: string;
  selectedStepId: string | null;
  preview: boolean;
  open: boolean;
  /** Path draft (used by new path steps and "follow this path"). */
  path: MotionPath;
  /** Animation a proposed plan was made for (null: the one on the timeline). */
  animationId: string | null;
  /** Recipe the plan was loaded from, for labelling. */
  recipeName: string | null;
  /** Bumped when the plan is replaced from outside (prompt proposal, recipe). */
  revision: number;
}

export const DEFAULT_PATH: MotionPath = {
  kind: 'bezier',
  points: [
    [0, 0],
    [120, -180],
    [300, -180],
    [420, 0],
  ],
  relative: true,
  orient: false,
  reverse: false,
};

const EMPTY: ComposerState = {
  slots: [],
  steps: [],
  seed: 1,
  totalSeconds: null,
  binding: {},
  policy: 'merge',
  allowExtend: false,
  newAnimationName: '',
  selectedStepId: null,
  preview: false,
  open: false,
  path: DEFAULT_PATH,
  animationId: null,
  recipeName: null,
  revision: 0,
};

function slotName(o: SceneObject, taken: ReadonlySet<string>): string {
  const base = `${o.type === 'character' ? 'char' : 'obj'}-${o.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 20) || 'x'}`;
  let name = base;
  for (let i = 2; taken.has(name); i++) name = `${base}-${i}`;
  return name;
}

export class MotionComposerStore extends Observable<ComposerState> {
  constructor() {
    super(EMPTY);
  }

  clear(): void {
    this.set({ ...EMPTY, revision: this.get().revision + 1 });
  }

  setOpen(open: boolean): void {
    this.update({ open });
  }

  /** The plan as a draft (for applying), or null when there is nothing to apply. */
  draft(): MotionDraft | null {
    const s = this.get();
    if (!s.steps.length) return null;
    return {
      plan: { version: 1, slots: s.slots, steps: s.steps, seed: s.seed, totalSeconds: s.totalSeconds },
      binding: s.binding,
      policy: s.policy,
      allowExtend: s.allowExtend,
      animationId: s.animationId,
    };
  }

  /** Loads an interpreted prompt plan for review (preview on). */
  propose(draft: MotionDraft): void {
    const plan = draft.plan;
    this.set({
      ...this.get(),
      slots: plan.slots.map((x) => ({ ...x })),
      steps: plan.steps.map((x) => structuredClone(x)),
      seed: plan.seed ?? 1,
      totalSeconds: plan.totalSeconds ?? null,
      binding: { ...draft.binding },
      policy: draft.policy,
      allowExtend: draft.allowExtend,
      animationId: draft.animationId,
      selectedStepId: plan.steps[0]?.id ?? null,
      preview: true,
      open: true,
      recipeName: null,
      revision: this.get().revision + 1,
    });
  }

  /** Loads a recipe with a binding (retargeting is validated when compiling). */
  loadRecipe(recipe: MotionRecipe, binding: Binding): void {
    this.set({
      ...this.get(),
      slots: recipe.plan.slots.map((x) => ({ ...x })),
      steps: recipe.plan.steps.map((x) => structuredClone(x)),
      seed: recipe.plan.seed,
      totalSeconds: recipe.plan.totalSeconds,
      binding: { ...binding },
      animationId: null,
      selectedStepId: recipe.plan.steps[0]?.id ?? null,
      preview: true,
      open: true,
      recipeName: recipe.name,
      revision: this.get().revision + 1,
    });
  }

  /** Slot bound to an object (created when missing). */
  private slotFor(state: ComposerState, o: SceneObject): { state: ComposerState; slot: string } {
    const existing = Object.entries(state.binding).find(([, id]) => id === o.id)?.[0];
    if (existing && state.slots.some((s) => s.name === existing)) return { state, slot: existing };
    const name = slotName(o, new Set(state.slots.map((s) => s.name)));
    return {
      slot: name,
      state: { ...state, slots: [...state.slots, { name, kind: o.type === 'character' ? 'character' : 'object', label: o.name }], binding: { ...state.binding, [name]: o.id } },
    };
  }

  /** Removes slots no step uses any more. */
  private prune(state: ComposerState): ComposerState {
    const used = new Set(state.steps.flatMap((s) => s.targets));
    const slots = state.slots.filter((s) => used.has(s.name));
    return { ...state, slots, binding: Object.fromEntries(Object.entries(state.binding).filter(([slot]) => used.has(slot))) };
  }

  /**
   * Adds a step for the given objects (compatible ones only). Returns an error message when
   * there is no compatible target.
   */
  addStep(template: TemplateId, objects: readonly SceneObject[]): string | null {
    const info = TEMPLATES[template];
    let state = this.get();
    const compatible = objects.filter((o) => !info.characterOnly || o.type === 'character');
    let targets: string[] = [];
    if (compatible.length) {
      for (const o of compatible) {
        const r = this.slotFor(state, o);
        state = r.state;
        targets.push(r.slot);
      }
    } else {
      const fallback = state.slots.find((s) => !info.characterOnly || s.kind === 'character');
      if (!fallback) return info.characterOnly ? `${info.label} needs a character: select a character instance on the canvas.` : `Select an object on the canvas to give “${info.label}” a target.`;
      targets = [fallback.name];
    }
    const step: MotionStepInput = { id: createId('st'), template, targets, params: {}, timing: { mode: 'sequence', delay: 0 }, ...(template === 'path' ? { path: structuredClone(state.path) } : {}) };
    this.set({ ...state, steps: [...state.steps, step], selectedStepId: step.id, open: true, recipeName: state.recipeName });
    return null;
  }

  updateStep(id: string, patch: Partial<MotionStepInput>): void {
    const s = this.get();
    this.set(this.prune({ ...s, steps: s.steps.map((x) => (x.id === id ? { ...x, ...patch } : x)) }));
  }

  setStepParam(id: string, key: string, value: number | string | boolean | null): void {
    const s = this.get();
    this.set({
      ...s,
      steps: s.steps.map((x) => {
        if (x.id !== id) return x;
        const params = { ...(x.params ?? {}) };
        if (value === null || value === '') delete params[key];
        else params[key] = value;
        return { ...x, params };
      }),
    });
  }

  /** Sets a step's targets to these objects (compatible ones; slots created as needed). */
  setTargets(id: string, objects: readonly SceneObject[]): string | null {
    let state = this.get();
    const step = state.steps.find((x) => x.id === id);
    if (!step) return null;
    const info = TEMPLATES[step.template];
    const compatible = objects.filter((o) => !info.characterOnly || o.type === 'character');
    if (!compatible.length) return info.characterOnly ? `${info.label} needs a character instance.` : 'Select at least one object.';
    const targets: string[] = [];
    for (const o of compatible) {
      const r = this.slotFor(state, o);
      state = r.state;
      targets.push(r.slot);
    }
    this.set(this.prune({ ...state, steps: state.steps.map((x) => (x.id === id ? { ...x, targets } : x)) }));
    return null;
  }

  removeStep(id: string): void {
    const s = this.get();
    const steps = s.steps.filter((x) => x.id !== id).map((x) => (x.timing?.ref === id ? { ...x, timing: { ...x.timing, mode: 'sequence' as const, ref: null } } : x));
    this.set(this.prune({ ...s, steps, selectedStepId: s.selectedStepId === id ? (steps[0]?.id ?? null) : s.selectedStepId, preview: steps.length ? s.preview : false }));
  }

  duplicateStep(id: string): void {
    const s = this.get();
    const i = s.steps.findIndex((x) => x.id === id);
    if (i < 0) return;
    const copy = { ...structuredClone(s.steps[i]!), id: createId('st'), timing: { mode: 'sequence' as const, delay: 0 } };
    this.set({ ...s, steps: [...s.steps.slice(0, i + 1), copy, ...s.steps.slice(i + 1)], selectedStepId: copy.id });
  }

  /** Moves a step up or down; references to steps that would come later are dropped. */
  moveStep(id: string, delta: number): void {
    const s = this.get();
    const i = s.steps.findIndex((x) => x.id === id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= s.steps.length) return;
    const steps = [...s.steps];
    [steps[i], steps[j]] = [steps[j]!, steps[i]!];
    const seen = new Set<string>();
    const fixed = steps.map((x) => {
      const ok = !x.timing?.ref || seen.has(x.timing.ref);
      seen.add(x.id);
      return ok ? x : { ...x, timing: { ...x.timing!, mode: 'sequence' as const, ref: null } };
    });
    this.set({ ...s, steps: fixed });
  }

  bind(slot: string, objectId: string): void {
    const s = this.get();
    this.set({ ...s, binding: { ...s.binding, [slot]: objectId } });
  }

  /** Keeps labels in step with the scene (renamed objects). */
  relabel(scene: Scene): void {
    const s = this.get();
    let changed = false;
    const slots = s.slots.map((slot) => {
      const o = scene.objects.find((x) => x.id === s.binding[slot.name]);
      if (o && o.name !== slot.label) {
        changed = true;
        return { ...slot, label: o.name };
      }
      return slot;
    });
    if (changed) this.update({ slots });
  }
}
